import React, { useState, useEffect, useRef } from "react";
import CryptoTokenRiverVisualizer from "../components/CryptoTokenRiverVisualizer";
import TokenCountGauge from "../components/TokenCountGauge";

export default function CryptoTokenRiverPage() {
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  const [tokenActive, setTokenActive] = useState(0);
  const [tokenFlux, setTokenFlux] = useState(0);

  const [riverSpeedPct, setRiverSpeedPct] = useState(100);
  const [densityPct, setDensityPct] = useState(100);
  const [chunkSize, setChunkSize] = useState(100);

  // ⚠️ `steps` A ÉTÉ RETIRÉ DE L'INTERFACE le 2026-07-29.
  //
  // C'était la LONGUEUR DU SEGMENT servi par `/animate` — un paramètre de
  // TRANSPORT, pas une commande produit. Posé en curseur à côté de
  // « Chunk Size », il donnait deux réglages d'apparence identique dont un
  // seul avait un sens pour l'opérateur.
  //
  // Et sa valeur par défaut était 20 : sous le modèle de continuation, cela
  // fait 20 × 0,03 s = 0,6 SECONDE de flux par segment. Le navigateur
  // redemandait donc une séquence toutes les six dixièmes de seconde.
  //
  // Le débit du flux se règle par « Densité », sa vitesse par « Vitesse
  // Rivière » — les deux entrent dans le MOTEUR. La longueur de segment est
  // désormais fixée par le hook.

  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  // Échantillon de tokens bruts, servi par `/continuous` — le produit textuel
  // du module, que la page n'affichait pas.
  const [echantillon, setEchantillon] = useState("");

  // Dernier instantané de flux remonté par le visualiseur. Dans un ref et non
  // dans un état : il est réécrit à chaque frame, un état déclencherait un
  // rendu par frame pour une donnée que seul le POST consomme.
  const snapshotRef = useRef(null);

  // --- Entropie UI (backend) ---
  useEffect(() => {
    const fetchEntropyUI = async () => {
      try {
        setLoadingUI(true);
        const res = await fetch("/api/geometry/crypto_token_river/entropy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            river_speed: riverSpeedPct / 100,
            density: densityPct / 100,
            chunk_size: chunkSize,
            chaos,
            glow,
            running,
          }),
        });
        const data = await res.json();
        if (data.entropy !== undefined) {
          setEntropyUI(data.entropy);
        }
      } catch (err) {
        console.error("Erreur fetch entropy UI CryptoTokenRiver:", err);
        setEntropyUI(null);
      } finally {
        setLoadingUI(false);
      }
    };
    fetchEntropyUI();
  }, [riverSpeedPct, densityPct, chunkSize, chaos, glow, running]);

  // --- Entropie Dynamique : l'état PHYSIQUE du flux affiché ---
  //
  // ⚠️ Ce POST n'envoyait AUCUNE position. Il transmettait deux curseurs et
  // trois champs tirés au sort — `turbulence`, `collisions`, `drift` — que la
  // route ignorait de toute façon, puisqu'elle fabriquait sa valeur à partir
  // d'un SHA-256 de `time + random.random() + os.urandom`.
  //
  // Il transporte désormais l'instantané réel : positions affichées, symboles
  // effectivement émis, et vitesses mesurées sur le déplacement de chaque
  // token — appariées par identifiant, puisque les indices se décalent dès
  // qu'un token sort du canal.
  useEffect(() => {
    let actif = true;
    const controller = new AbortController();

    const envoyer = () => {
      const snap = snapshotRef.current;
      if (!snap || !snap.positions || snap.positions.length < 2) return;

      fetch("/api/geometry/crypto_token_river/dyn_entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(snap),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          if (actif && typeof data.entropy_dyn === "number") {
            setEntropyDyn(data.entropy_dyn);
          }
        })
        .catch(() => {});
    };

    const id = setInterval(envoyer, 1000);
    return () => {
      actif = false;
      clearInterval(id);
      controller.abort();
    };
  }, []);

  // --- Échantillon de tokens bruts — le PRODUIT du module ---
  //
  // ⚠️ Le curseur « Chunk Size » n'avait plus de cible après la refonte : dans
  // le modèle de flux, c'est `density` qui règle le débit. Plutôt que de le
  // laisser inerte — une perte de manipulabilité — il pilote la taille de
  // l'échantillon servi par `/continuous`, qui est la sortie TEXTUELLE réelle
  // de la source. La page montre enfin ce que le module produit.
  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/geometry/crypto_token_river/continuous?chunk_size=${chunkSize}`,
          { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.tokens)) {
          setEchantillon(data.tokens.map((t) => t.char).join(""));
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [chunkSize]);

  // ⚠️ Le hook n'est plus appelé ICI : le composant le consomme lui-même, comme
  // sur les sept autres modules. La page le sollicitait en parallèle et ne
  // transmettait jamais les frames obtenues au visualiseur — celui-ci affichait
  // donc une rivière entièrement inventée, pendant que le résultat de
  // `/animate` était récupéré puis jeté.
  //
  // Le callback `handleEntropyUpdate` a disparu avec la pseudo-mesure qu'il
  // relayait : `accumulator` valait un `Math.random() * 10` pondéré par les
  // curseurs, et le champ `shannon` qui l'accompagnait était littéralement
  // `Math.random() * tokenCount`.

  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div className="page-container">
      <h1>Crypto Token River Visualizer</h1>

      <div className="gauges-row">
        <div className="card small">
          <h2>Entropie UI</h2>
          <div className="value ui">
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div className="value dyn">
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card small">
          <h2 style={{ color: "#29b6f6" }}>Score Total</h2>
          <div className="value total">{entropyTotal}</div>
        </div>
        <div className="card small" style={{ textAlign: "center" }}>
          <h2 style={{ color: "#ffd600" }}>Tokens Actifs</h2>
          <TokenCountGauge value={tokenActive} max={2000} />
        </div>
        <div className="card small">
          <h2 style={{ color: "#ff9100" }}>Flux Tokens</h2>
          <div className="value">{tokenFlux}</div>
        </div>
      </div>

      <div className="controls-row">
        {/* --- Panneau de contrôles --- */}
        <div className="controls-panel">
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            className={
              chaos ? "toggle-button active-chaos" : "toggle-button inactive-chaos"
            }
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            className={
              glow ? "toggle-button active-glow" : "toggle-button inactive-glow"
            }
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          <div className="input-group">
            <label>Vitesse Rivière : {riverSpeedPct}%</label>
            <input
              type="range"
              min={10}
              max={200}
              value={riverSpeedPct}
              onChange={(e) => setRiverSpeedPct(Number(e.target.value))}
            />
          </div>
          <div className="input-group">
            <label>Densité Tokens : {densityPct}%</label>
            <input
              type="range"
              min={10}
              max={200}
              value={densityPct}
              onChange={(e) => setDensityPct(Number(e.target.value))}
            />
          </div>
          <div className="input-group">
            <label>Chunk Size : {chunkSize}</label>
            <input
              type="range"
              min={10}
              max={500}
              value={chunkSize}
              onChange={(e) => setChunkSize(Number(e.target.value))}
            />
          </div>

          {/* Sortie textuelle réelle de la source — la cible du curseur
              ci-dessus. Sans elle, « Chunk Size » n'agirait sur rien de
              visible depuis que le débit du flux est piloté par « Densité ». */}
          <div className="input-group">
            <label>Échantillon émis ({echantillon.length} car.)</label>
            <div
              style={{
                fontFamily: "monospace",
                fontSize: "0.72rem",
                lineHeight: 1.35,
                wordBreak: "break-all",
                maxHeight: 96,
                overflowY: "auto",
                padding: "6px 8px",
                borderRadius: 6,
                background: "#111",
                color: "#0ff",
              }}
            >
              {echantillon || "⏳"}
            </div>
          </div>
        </div>

        {/* --- Conteneur stylisé du visualizer --- */}
        <div className="crypto-river-wrapper">
          <CryptoTokenRiverVisualizer
            // Key retirée pour éviter le remontage brutal du canvas lors du slider
            running={running}
            chaos={chaos}
            glow={glow}
            riverSpeedPct={riverSpeedPct}
            densityPct={densityPct}
            onActiveCountUpdate={(count) => {
              setTokenActive(count);
              setTokenFlux(count);
            }}
            // Conserve le dernier instantané pour le prochain POST. Le
            // composant ne calcule plus d'entropie : il n'a rien à en dire.
            onSnapshot={(snap) => {
              snapshotRef.current = snap;
            }}
          />
        </div>
      </div>
    </div>
  );
}
